import {
  DataBlockVariant,
  HL7MessageGroup,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { IconDropDownButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/IconDropDownButton';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  isMiniscreen,
  openWithMiniscreen,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import {
  activateTab,
  createTabInBackground,
} from '@lmt/unicorn-shared/app/features/navigation/operations';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import { ReactComponent as OptionIcon } from '@lmt/unicorn-shared/app/icons/option.svg';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import styles from './DataBlockOptionButton.module.scss';
import { toast } from 'react-toastify';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { copyPreviousValues } from '@lmt/unicorn-shared/app/components/measurements/copy-datablock-field/copy-previous-values-async-helper';
import { useEventCallback } from '@mui/material';
import { isEmpty } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { showSelectOptionsModal } from '@lmt/unicorn-shared/app/components/common/Dialog/Dialog';
import { useTranslation } from 'react-i18next';
import { getSharedFieldNames } from './delete-data-block-helper';
import { snakeCase } from 'lodash';
import { PatientProfileContext } from '../../../types';
import { useContextSelector } from 'use-context-selector';

interface DataBlockOptionButtonProps {
  patientId: number;
  dataBlockId: number;
  dataBlockTypeId: number;
  isGDTEnabled: boolean;
  isHL7Enabled: boolean;
  isNotDeletable: boolean;
  isMiniscreenEnabled: boolean;
}

export const DataBlockOptionButton: React.FC<DataBlockOptionButtonProps> = (
  props,
) => {
  const {
    dataBlockId,
    isGDTEnabled,
    isHL7Enabled,
    isMiniscreenEnabled,
    isNotDeletable,
    dataBlockTypeId,
    patientId,
  } = props;
  const i18next = useScopedTranslation('patients.cases');
  const i18nextNotifications = useScopedTranslation('patients.notifications');
  const i18nextGlobal = useTranslation();
  const i18nDictionaries = useTranslation(NS_DataBlockFields);
  const dispatch = useUniDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const claims = useClaims();
  const forms = useGlobalForms();
  const dataBlockTypes = useEnabledDataBlockTypes();
  const modals = useModal();

  const setPatientDataBlocksHasUnsavedChanges = useContextSelector(
    PatientProfileContext,
    (v) => v.setUnsavedChanges,
  );

  const removeDataBlock = useEventCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18next.t('confirmations.delete_datablock_title'),
      text: i18next.t('confirmations.delete_datablock_text'),
      allowStyleTagsInText: true,
      okButtonText: i18next.t('confirmations.delete_datablock_ok_button'),
      okButtonColor: ButtonColor.SuperDanger,
    });
    if (isConfirmed) {
      // Stop detecting unsaved changes for a deleted data block
      setPatientDataBlocksHasUnsavedChanges(`${dataBlockTypeId}`, false);
      const dataBlockType = dataBlockTypes.find(
        (type) => type.id === dataBlockTypeId,
      );
      const sharedFieldNames = await getSharedFieldNames(
        queryClient,
        dataBlockId,
        dataBlockType,
      );
      let includeSharedValues = false;
      if (!isEmpty(sharedFieldNames)) {
        const localizedFieldNamesList = sharedFieldNames
          .map((name) => {
            const localizationKey =
              snakeCase(DataBlockVariant[dataBlockType!.variant]) +
              '.shared.' +
              name.toLowerCase();
            return i18nDictionaries.t(localizationKey, {
              defaultValue: name,
            });
          })
          .join(', ');
        includeSharedValues =
          (await modals.showConfirm({
            title: i18nextGlobal.t(
              'data_blocks.confirmations.include_shared_values.title',
            ),
            text: i18nextGlobal.t(
              'data_blocks.confirmations.include_shared_values.text',
              {
                fields: localizedFieldNamesList,
              },
            ),
            okButtonText: i18nextGlobal.t(
              'data_blocks.confirmations.include_shared_values.ok_text',
            ),
            cancelButtonText: i18nextGlobal.t(
              'data_blocks.confirmations.include_shared_values.cancel_text',
            ),
          })) ?? false;
      }
      await ClientFactory.DataBlockClient.remove(dataBlockId, {
        includeSharedValues: !!includeSharedValues,
      });
    }
    await queryClient.invalidateQueries({
      queryKey: await QueryFactory.DataBlockQuery.getQueryKey(),
    });
  });

  const openDataBlockEditor = useEventCallback(() => {
    const dataBlockType = dataBlockTypes.find((t) => t.id === dataBlockTypeId);
    const search = `dataBlockTypeId=${dataBlockTypeId}&dataBlockVariant=${dataBlockType?.variant}`;
    const tab = createTabInBackground(
      pages.dataBlockSettings.link(),
      dispatch,
      search,
    );
    if (tab) {
      // tab change must be delayed, otherwise drag and drop doesn't work
      setTimeout(() => activateTab(tab, navigate), 100);
    }
  });

  const { data: patient } =
    QueryFactory.PatientQuery.useGetPatientQuery(patientId);
  const { data: hl7Templates } =
    QueryFactory.HL7OutgoingTemplatesQuery.useGetQuery({
      enabled: claims.permissions.hl7,
    });
  const { data: hl7TemplateRules } =
    QueryFactory.HL7TemplateRulesQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
        isActive: true,
        messageGroup: HL7MessageGroup.DataBlock,
        dataBlockTypeId: dataBlockTypeId,
      },
      {
        enabled: claims.permissions.hl7 && !!patient?.subtenantId,
      },
    );

  const hl7TemplateOptions = useMemo(() => {
    const hl7TemplateIds = (hl7TemplateRules ?? []).map(
      (rule) => rule.documentTemplateId,
    );
    return (hl7Templates ?? [])
      .filter((x) => hl7TemplateIds.includes(x.id))
      .map((x) => {
        return {
          id: x.id,
          value: x.title,
        };
      });
  }, [hl7TemplateRules, hl7Templates]);

  const sendViaHL7 = useCallback(async () => {
    const toastId = createId();
    try {
      let templateIds = hl7TemplateOptions.map((x) => x.id);
      if (hl7TemplateOptions.length > 1) {
        const result = await showSelectOptionsModal(modals, {
          title: i18next.t('confirmations.select_hl7_templates_title'),
          text: i18next.t('confirmations.select_hl7_templates_text'),
          okButtonText: i18next.t('confirmations.select_hl7_templates_ok_text'),
          options: hl7TemplateOptions,
          selectedOptions: hl7TemplateOptions,
        });
        if (isEmpty(result)) {
          return;
        }
        templateIds = result!.map((x) => x.id);
      }
      toast(i18nextNotifications.t('hl7_message_creating'), {
        type: 'default',
        autoClose: false,
        toastId: toastId,
      });

      await ClientFactory.HL7OutgoingMessageClient.sendMeasurementMessage(
        dataBlockId,
        {
          templateIds,
        },
      );
      toast.update(toastId, {
        render: i18nextNotifications.t('hl7_message_created'),
        type: 'success',
        autoClose: 4000,
      });
      await queryClient.invalidateQueries({
        queryKey: await QueryFactory.DataBlockQuery.getQueryKey(),
      });
    } catch (error) {
      toast.update(toastId, {
        autoClose: 100,
      });
      await modals.showError({ text: convertToErrorString(error) });
    }
  }, [queryClient, dataBlockId, hl7TemplateOptions]);

  const fillWithPrevious = useCallback(async () => {
    try {
      const isCopied = await copyPreviousValues(
        dataBlockId,
        forms.openCopyDataBlockValue,
        modals,
        queryClient,
      );
      if (isCopied) {
        toast(i18next.t('toasts.field_values_copied'), { type: 'success' });
      } else {
        toast(i18next.t('toasts.no_data_to_copy'), { type: 'warning' });
      }
    } catch (error) {
      await modals.showError({ text: convertToErrorString(error) });
    }
  }, [dataBlockId, forms, dispatch, queryClient]);

  const options = [
    {
      value: 'fill_with_previous_values',
      displayValue: i18next.t('options.fill_with_previous_values'),
      onClick: fillWithPrevious,
      enabled: claims.permissions.dataBlockManagement,
    },
    {
      value: 'send_to_miniscreen',
      displayValue: i18next.t('options.open_in_miniscreen'),
      onClick: () => openWithMiniscreen({ measurementId: dataBlockId }),
      enabled: isMiniscreenEnabled && isMiniscreen(),
    },
    {
      value: 'send_via_gdt',
      displayValue: i18next.t('options.send_via_gdt'),
      onClick: () => forms.openGDTSending(dataBlockId),
      enabled: isGDTEnabled && claims.permissions.gdt,
    },
    {
      value: 'send_via_hl_7',
      displayValue: i18next.t('options.send_via_hl7'),
      onClick: sendViaHL7,
      enabled: isHL7Enabled && hl7TemplateOptions.length > 0,
    },
    {
      value: 'open_ui_editor',
      displayValue: i18next.t('options.open_ui_editor'),
      onClick: openDataBlockEditor,
      enabled: claims.permissions.developerMode,
    },
    {
      value: 'delete',
      displayValue: i18next.t('options.delete'),
      onClick: removeDataBlock,
      enabled: !isNotDeletable && claims.permissions.dataBlockManagement,
    },
  ].filter((option) => option.enabled);

  if (options.length === 0) return null;

  return (
    <IconDropDownButton
      className={styles.optionButton}
      testId="data-block-options-button"
      iconComponent={<OptionIcon className={styles.icon} />}
      options={options}
      optionListClassName={styles.optionListContainer}
    />
  );
};
